import type { OrganizationDetail } from '@rtkwlf/eloc';
import {
  redirect,
  type ShouldRevalidateFunctionArgs,
  type LoaderFunction,
} from 'react-router-dom';

import { getUser, verifyUser } from '@platform/core/auth';
import { ForbiddenError, InternalServerError } from '@platform/core/errors';
import { queryClient } from '@platform/core/state/query-client';

import { capabilitiesQuery } from '@apis/capability/queries/capabilities';
import { systemCapabilitiesQuery } from '@apis/capability/queries/system-capabilities';
import { organizationQuery as elocOrganizationQuery } from '@apis/eloc/queries/organization';
import { capabilitiesQuery as rendallCapabilitiesQuery } from '@apis/rendall/queries/capabilities';
import { organizationQuery } from '@apis/rendall/queries/organization';
import { subscriptionsQuery } from '@apis/rendall/queries/subscriptions';
import { userInfoQuery } from '@apis/rendall/queries/user-info';
import { initializeRendallApiClient } from '@apis/rendall/rendall-api-client';

import { isUuidFormat } from '../../utils/is-uuid-format/is-uuid-format';

// avoid re-triggering loader when search param changes
export function shouldRevalidateOrganization({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
  if (currentUrl.pathname === nextUrl.pathname) {
    return false;
  } else {
    return defaultShouldRevalidate;
  }
}

export const organizationLoader: LoaderFunction = async ({
  params,
  request,
}) => {
  const { organizationId } = params;
  if (!organizationId)
    throw new Error('This loader requires the parameter organizationId');

  // wait for user to be authenticated before moving further.
  await verifyUser();

  // If org id is not a valid uuid format, redirect to root path to set one.
  if (!isUuidFormat(organizationId)) {
    const url = new URL(request.url);

    // keep track of the original path so that we can redirect after user has selected an org id.
    // - prevent bookmarked urls from failing with the org id now being part of the url.
    // - accommodates urls created by S2 that doesn't contain any org id search params.
    return redirect(`/?from=${encodeURIComponent(url.pathname + url.search)}`);
  }

  let organization: OrganizationDetail;

  try {
    organization = await queryClient.fetchQuery(
      elocOrganizationQuery(organizationId),
    );
  } catch (error: unknown) {
    const fetchError = error as any; // instanceof ResponseError does not work here
    const status = fetchError?.response?.status;

    if (status >= 400 && status < 500) {
      const user = await getUser();

      throw new ForbiddenError({
        user,
        reason: 'ORGANIZATION_ACCESS_DENIED',
        showAction: true,
      });
    }

    throw new InternalServerError();
  }

  await initializeRendallApiClient(organization.pod);

  // Note: prefetch the queries for `useOrganizationAccess`
  // Start prefetching but don't wait for completion
  queryClient.prefetchQuery(organizationQuery(organizationId));
  queryClient.prefetchQuery(capabilitiesQuery(organizationId));
  queryClient.prefetchQuery(userInfoQuery());
  queryClient.prefetchQuery(rendallCapabilitiesQuery(organizationId));
  queryClient.prefetchQuery(subscriptionsQuery(organizationId));
  queryClient.prefetchQuery(systemCapabilitiesQuery());

  return null;
};
